import { useEffect, useRef, useState } from "react";
import { Download, FileText, Plus, SpinnerGap, Trash, UploadSimple } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { DOC_CATEGORIES, docCategoryLabel, formatDate } from "@/lib/constants";
import { toast } from "@/components/ui/sonner";

function formatSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

export default function AdminDocuments() {
  const inputRef = useRef(null);
  const [docs, setDocs] = useState(null);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("pv_ag");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/documents").then((r) => setDocs(r.data)).catch(() => setDocs([]));
  }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!title.trim() || !file) {
      toast.error("Titre et fichier sont requis");
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", title);
      fd.append("category", category);
      const { data } = await api.post("/admin/documents", fd);
      setDocs((d) => [data, ...(d || [])]);
      setTitle("");
      setCategory("pv_ag");
      setFile(null);
      if (inputRef.current) inputRef.current.value = "";
      toast.success(`« ${data.title} » publié`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setSaving(false);
  };

  const remove = async (d) => {
    try {
      await api.delete(`/admin/documents/${d.id}`);
      setDocs((list) => list.filter((x) => x.id !== d.id));
      toast.success(`« ${d.title} » retiré`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const inputCls =
    "w-full rounded-2xl border border-black/10 px-4 py-3 text-sm outline-none transition-colors focus:border-primary";

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_1.2fr]" data-testid="admin-documents-tab">
      <form onSubmit={add} className="h-fit rounded-3xl border border-black/5 bg-white p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
        <h2 className="font-display text-lg font-bold tracking-tight">Publier un document</h2>
        <p className="mt-1 text-xs text-muted-foreground">
          PV d'assemblée, règlement… téléchargeable par les résidents sur la page Infos. PDF, Word, Excel ou image, 15 Mo max.
        </p>
        <div className="mt-5 space-y-3">
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Titre (ex. PV de l'AG du 12 mars 2026)" data-testid="doc-title-input" className={inputCls} />
          <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="doc-category-select" className={inputCls}>
            {DOC_CATEGORIES.map((c) => (
              <option key={c.key} value={c.key} data-testid={`doc-category-${c.key}`}>{c.label}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            data-testid="doc-file-button"
            className="flex w-full items-center justify-center gap-2 rounded-2xl border border-dashed border-black/15 px-4 py-6 text-sm font-semibold text-muted-foreground transition-colors hover:border-primary hover:text-primary"
          >
            <UploadSimple size={18} weight="duotone" />
            {file ? file.name : "Choisir un fichier"}
          </button>
          <input
            ref={inputRef}
            type="file"
            hidden
            accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
            data-testid="doc-file-input"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
          />
        </div>
        <button
          type="submit"
          disabled={saving}
          data-testid="doc-add-button"
          className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-primary px-6 py-3.5 text-sm font-bold text-primary-foreground transition-colors hover:bg-[#265442] disabled:opacity-60"
        >
          {saving ? <SpinnerGap size={16} className="animate-spin" /> : <Plus size={16} weight="bold" />}
          Publier le document
        </button>
      </form>

      <div className="space-y-3" data-testid="admin-documents-list">
        {docs === null && <div className="h-24 animate-pulse rounded-3xl bg-black/5" />}
        {docs !== null && docs.length === 0 && (
          <p className="rounded-3xl border border-black/5 bg-white p-8 text-center text-sm text-muted-foreground">
            Aucun document publié pour le moment.
          </p>
        )}
        {docs?.map((d) => (
          <div key={d.id} className="flex items-center gap-4 rounded-3xl border border-black/5 bg-white p-4 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid={`admin-doc-${d.id}`}>
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
              <FileText size={18} weight="duotone" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-bold">{d.title}</p>
              <p className="truncate text-xs text-muted-foreground">
                {docCategoryLabel(d.category)} · {formatDate(d.created_at)} {d.size ? `· ${formatSize(d.size)}` : ""}
              </p>
            </div>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/documents/${d.id}/download`}
              data-testid={`doc-download-${d.id}`}
              aria-label={`Télécharger ${d.title}`}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-black/10 text-foreground/50 transition-colors hover:border-primary hover:text-primary"
            >
              <Download size={16} weight="duotone" />
            </a>
            <button
              onClick={() => remove(d)}
              data-testid={`doc-delete-${d.id}`}
              aria-label={`Retirer ${d.title}`}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-black/10 text-foreground/50 transition-colors hover:border-[#C90A0A] hover:text-[#C90A0A]"
            >
              <Trash size={16} weight="duotone" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
